import React, { useState, useEffect, useCallback } from 'react';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import {
  Container,
  Box,
  Button,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import menuService from '../services/menuService';
import recipeService from '../services/recipeService';
import ingredientService from '../services/ingredientService';
import expenseService from '../services/expenseService';
import DataTable from '../components/DataTable';

const MenusPage = () => {
  const { t } = useTranslation();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [recipeDialogOpen, setRecipeDialogOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeTabValue, setRecipeTabValue] = useState(0);
  //const [ingredients, setIngredients] = useState([]);
  //const [expensesData, setExpensesData] = useState([]);
  const [loadingRecipeData, setLoadingRecipeData] = useState(false);
  const [recipeFormData, setRecipeFormData] = useState({
    baseMembers: 100,
    ingredients: [],
    expenses: []
  });
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [availableExpenses, setAvailableExpenses] = useState([]);
  const [formData, setFormData] = useState({
    name_en: '',
    name_ta: '',
    category: 'breakfast',
    description_en: '',
    description_ta: '',
    status: 'active'
  });

  const fetchMenus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await menuService.getMenus(page, limit);
      setMenus(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching menus');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  const handleSearch = async (query = null) => {
    const queryToUse = query !== null ? query : searchQuery;
    
    if (!queryToUse.trim()) {
      fetchMenus();
      return;
    }

    setLoading(true);
    try {
      const response = await menuService.searchMenus(queryToUse, 1, limit);
      setMenus(response.data.data);
      setTotal(response.data.pagination.total);
      setPage(1);
    } catch (err) {
      setError(err.response?.data?.message || 'Error searching menus');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    // Trigger search as user types (real-time)
    if (query.trim() || query === '') {
      handleSearch(query);
    }
  };

  const handleAddClick = () => {
    setSelectedMenu(null);
    setFormData({
      name_en: '',
      name_ta: '',
      category: 'breakfast',
      description_en: '',
      description_ta: '',
      status: 'active'
    });
    setDialogOpen(true);
  };

  const handleEditClick = (menu) => {
    setSelectedMenu(menu);
    setFormData(menu);
    setDialogOpen(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      if (selectedMenu) {
        await menuService.updateMenu(selectedMenu._id, formData);
      } else {
        await menuService.createMenu(formData);
      }
      setDialogOpen(false);
      setError('');
      fetchMenus();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving menu');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await menuService.deleteMenu(deleteId);
      setDeleteConfirmOpen(false);
      setError('');
      fetchMenus();
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting menu');
    }
  };

  // Recipe handling functions
  const fetchRecipeData = async (menuId) => {
  setLoadingRecipeData(true);
  setRecipeTabValue(0);

  try {
    const [recipeRes, ingredientsRes, expensesRes] = await Promise.all([
      recipeService.getRecipeByMenuId(menuId),
      ingredientService.getIngredients(1, 1000),
      expenseService.getExpenses(1, 1000)
    ]);

    const recipe = recipeRes.data.data;

    setSelectedRecipe(recipe);

    setRecipeFormData({
      baseMembers: recipe.baseMembers || 100,
      ingredients: recipe.ingredients || [],
      expenses: recipe.expenses || []
    });

    setAvailableIngredients(ingredientsRes.data.data || []);
    setAvailableExpenses(expensesRes.data.data || []);

    setRecipeDialogOpen(true);
  } catch (err) {
    setRecipeDialogOpen(false);
    setError(err.response?.data?.message || 'Error fetching recipe data');
  } finally {
    setLoadingRecipeData(false);
  }
};

  const handleBaseMembersChange = (e) => {
    setRecipeFormData(prev => ({
      ...prev,
      baseMembers: parseInt(e.target.value) || 100
    }));
  };

  const handleIngredientQuantityChange = (index, value) => {
    setRecipeFormData(prev => {
      const newIngredients = [...prev.ingredients];
      newIngredients[index] = {
        ...newIngredients[index],
        quantity: parseFloat(value) || 0
      };
      return { ...prev, ingredients: newIngredients };
    });
  };

  const handleRemoveIngredient = (index) => {
    setRecipeFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleAddIngredient = (ingredient) => {
    if (!recipeFormData.ingredients.find(ing => ing.ingredientId === ingredient._id)) {
      setRecipeFormData(prev => ({
        ...prev,
        ingredients: [
          ...prev.ingredients,
          {
            ingredientId: ingredient._id,
            ingredientName_en: ingredient.name_en,
            ingredientName_ta: ingredient.name_ta,
            quantity: 0,
            unit: ingredient.unit || 'kg'
          }
        ]
      }));
    }
  };

  const handleRemoveExpense = (index) => {
    setRecipeFormData(prev => ({
      ...prev,
      expenses: prev.expenses.filter((_, i) => i !== index)
    }));
  };

  const handleAddExpense = (expense) => {
    if (!recipeFormData.expenses.find(exp => exp.expenseId === expense._id)) {
      setRecipeFormData(prev => ({
        ...prev,
        expenses: [
          ...prev.expenses,
          {
            expenseId: expense._id,
            amount: 0
          }
        ]
      }));
    }
  };

  const handleExpenseAmountChange = (index, value) => {
    setRecipeFormData(prev => {
      const newExpenses = [...prev.expenses];
      newExpenses[index] = {
        ...newExpenses[index],
        amount: parseFloat(value) || 0
      };
      return { ...prev, expenses: newExpenses };
    });
  };

  const handleSaveRecipe = async () => {
    try {
      if (selectedRecipe) {
        await recipeService.updateRecipe(selectedRecipe._id, {
menuId:
  typeof selectedRecipe.menuId === 'object'
    ? selectedRecipe.menuId._id
    : selectedRecipe.menuId,          baseMembers: recipeFormData.baseMembers,
          ingredients: recipeFormData.ingredients,
          expenses: recipeFormData.expenses
        });
        setRecipeDialogOpen(false);
        setError('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving recipe');
    }
  };

  const columns = [
    { id: 'name_en', label: t('menus.nameEnglish') },
    { id: 'name_ta', label: t('menus.nameTamil') },
    { id: 'category', label: t('menus.category') },
    { id: 'status', label: t('menus.status') },
    {
      id: 'actions',
      label: 'Actions',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleEditClick(row)}
            color="primary"
            title="Edit Menu Details"
          >
            <EditIcon />
          </IconButton>
          <IconButton
        size="small"
        color="secondary"
        title="Edit Recipe"
        onClick={() => fetchRecipeData(row._id)}
      >
        <MenuBookIcon />
      </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(row._id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <TextField
          placeholder={t('common.search')}
          value={searchQuery}
          onChange={handleSearchInputChange}
          size="small"
          fullWidth
          variant="outlined"
        />
        <Button variant="contained" color="success" onClick={handleAddClick}>
          {t('menus.addMenu')}
        </Button>
      </Box>

      <DataTable
        columns={columns}
        rows={menus}
        loading={loading}
        pagination={{ page, limit, total }}
        onPageChange={setPage}
        onRowsPerPageChange={setLimit}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedMenu ? t('menus.editMenu') : t('menus.addMenu')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('menus.nameEnglish')}
            name="name_en"
            value={formData.name_en}
            onChange={handleFormChange}
            fullWidth
            required
          />
          <TextField
            label={t('menus.nameTamil')}
            name="name_ta"
            value={formData.name_ta}
            onChange={handleFormChange}
            fullWidth
            required
          />
          <FormControl fullWidth>
            <InputLabel>{t('menus.category')}</InputLabel>
            <Select
              name="category"
              value={formData.category}
              onChange={handleFormChange}
              label={t('menus.category')}
            >
              <MenuItem value="breakfast">{t('menus.categories.breakfast')}</MenuItem>
              <MenuItem value="lunch">{t('menus.categories.lunch')}</MenuItem>
              <MenuItem value="dinner">{t('menus.categories.dinner')}</MenuItem>
              <MenuItem value="snacks">{t('menus.categories.snacks')}</MenuItem>
              <MenuItem value="sweets">{t('menus.categories.sweets')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t('common.description') + ' (English)'}
            name="description_en"
            value={formData.description_en}
            onChange={handleFormChange}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label={t('common.description') + ' (Tamil)'}
            name="description_ta"
            value={formData.description_ta}
            onChange={handleFormChange}
            fullWidth
            multiline
            rows={2}
          />
          <FormControl fullWidth>
            <InputLabel>{t('menus.status')}</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleFormChange}
              label={t('menus.status')}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this menu?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recipe Edit Dialog */}
      <Dialog open={recipeDialogOpen} onClose={() => setRecipeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedRecipe ? `Edit Menu Items - ${selectedRecipe.menuName_en}` : 'Loading...'}
        </DialogTitle>
        {loadingRecipeData ? (
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </DialogContent>
        ) : (
          <>
            <DialogContent sx={{ pt: 2 }}>
              <TextField
                label="Base Members (for scaling)"
                type="number"
                value={recipeFormData.baseMembers}
                onChange={handleBaseMembersChange}
                fullWidth
                size="small"
                sx={{ mb: 3 }}
              />

              <Tabs value={recipeTabValue} onChange={(e, newValue) => setRecipeTabValue(newValue)} sx={{ mb: 2 }}>
                <Tab label={`Ingredients (${recipeFormData.ingredients.length})`} />
                <Tab label={`Expenses (${recipeFormData.expenses.length})`} />
              </Tabs>

              {/* Ingredients Tab */}
              {recipeTabValue === 0 && (
                <Box>
                  {recipeFormData.ingredients.length > 0 && (
                    <Table size="small" sx={{ mb: 3 }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell>Ingredient</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="center">Unit</TableCell>
                          <TableCell align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recipeFormData.ingredients.map((ing, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Typography variant="body2">
                                {ing.ingredientName_en}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ({ing.ingredientName_ta})
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={ing.quantity}
                                onChange={(e) => handleIngredientQuantityChange(idx, e.target.value)}
                                inputProps={{ step: '0.01' }}
                                sx={{ width: '80px' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">{ing.unit}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveIngredient(idx)}
                                color="error"
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}

                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Add Ingredients
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1 }}>
                    {availableIngredients.map((ing) => (
                      <Button
                        key={ing._id}
                        variant="outlined"
                        size="small"
                        onClick={() => handleAddIngredient(ing)}
                        disabled={recipeFormData.ingredients.some(i => i.ingredientId === ing._id)}
                        sx={{ justifyContent: 'flex-start' }}
                      >
                        <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
                        {ing.name_en}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Expenses Tab */}
              {recipeTabValue === 1 && (
                <Box>
                  {recipeFormData.expenses.length > 0 && (
                    <Table size="small" sx={{ mb: 3 }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell>Expense</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recipeFormData.expenses.map((exp, idx) => {
                          const expenseDetail = availableExpenses.find(e => e._id === exp.expenseId);
                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                <Typography variant="body2">
                                  {expenseDetail?.name_en || 'Unknown'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={exp.amount}
                                  onChange={(e) => handleExpenseAmountChange(idx, e.target.value)}
                                  inputProps={{ step: '0.01' }}
                                  sx={{ width: '100px' }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveExpense(idx)}
                                  color="error"
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}

                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Add Expenses
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1 }}>
                    {availableExpenses.map((exp) => (
                      <Button
                        key={exp._id}
                        variant="outlined"
                        size="small"
                        onClick={() => handleAddExpense(exp)}
                        disabled={recipeFormData.expenses.some(e => e.expenseId === exp._id)}
                        sx={{ justifyContent: 'flex-start' }}
                      >
                        <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
                        {exp.name_en}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRecipeDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveRecipe} variant="contained" color="primary">
                Save Recipe
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default MenusPage;
